import { type ApiError, type ProgramPublic, ProgramsService } from "@/client"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { toaster } from "@/components/ui/toaster"
import { Button, Text } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"

interface DeleteProgramProps {
  program: ProgramPublic
  onClose: () => void
}

export function DeleteProgram({ program, onClose }: DeleteProgramProps) {
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: () => ProgramsService.deleteProgram({ programId: program.id }),
    onSuccess: () => {
      toaster.create({
        title: "Program deleted successfully",
        type: "success",
      })
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      onClose()
    },
    onError: (err: ApiError) => {
      toaster.create({
        title: "Failed to delete program",
        description: err.message,
        type: "error",
      })
    },
  })

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  return (
    <DialogRoot
      open={true}
      onOpenChange={(e) => !e.open && onClose()}
      placement="center"
      size={{ base: "xs", md: "md" }}
      role="alertdialog"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Program</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          <Text>
            Are you sure you want to delete the program{" "}
            <strong>{program.name}</strong>? This action cannot be undone.
          </Text>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette="red"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
